import React, { useEffect, useState, useRef } from 'react';
import { Card, Button, Badge } from '../components/UI';
import { apiRequest } from '../utils/api';
import { User, Team } from '../types';
import { Save, Trash2, UserPlus, RefreshCw, Users, ShieldAlert, Flag, Search, X, ChevronDown } from 'lucide-react';
import { useLeague } from '../components/LeagueContext';
import { PageHero } from '../components/PageHero';

interface Assignment {
    id: string;
    user_id: string;
    team_id: string | null;
    is_reserve: number; // 0 or 1
    username: string;
    ea_id: string;
}

interface SeasonData {
    teams: Team[];
    assignments: Assignment[];
    users: Partial<User>[];
    seasonId: string;
    seasonName: string;
}

// --- AUTOCOMPLETE COMPONENT ---
const DriverAutocomplete: React.FC<{
    users: Partial<User>[];
    onSelect: (userId: string) => void;
    placeholder?: string;
    className?: string;
    autoFocus?: boolean;
}> = ({ users, onSelect, placeholder, className, autoFocus }) => {
    const [search, setSearch] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filtered = users.filter(u => 
        (u.username || '').toLowerCase().includes(search.toLowerCase()) || 
        (u.eaId || '').toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className={`relative w-full ${className}`} ref={wrapperRef}>
            <div className="relative">
                <input 
                    className="w-full bg-gray-900 border border-gray-600 rounded p-2 pl-8 text-sm text-white focus:border-f1-red outline-none transition-colors placeholder-gray-600"
                    placeholder={placeholder || "Namen eingeben …"}
                    value={search}
                    onChange={e => { setSearch(e.target.value); setIsOpen(true); }}
                    onFocus={() => setIsOpen(true)}
                    autoFocus={autoFocus}
                />
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                {search && (
                    <button 
                        onClick={() => { setSearch(''); setIsOpen(false); }} 
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                    >
                        <X size={14}/>
                    </button>
                )}
            </div>

            {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-600 rounded-md shadow-xl z-50 max-h-60 overflow-y-auto">
                    {filtered.length === 0 ? (
                        <div className="p-3 text-xs text-gray-500 italic text-center">Keine Fahrer gefunden</div>
                    ) : (
                        filtered.map(u => (
                            <div 
                                key={u.id}
                                onClick={() => {
                                    onSelect(u.id!);
                                    setSearch('');
                                    setIsOpen(false);
                                }}
                                className="px-3 py-2 hover:bg-gray-700 cursor-pointer border-b border-gray-700 last:border-0 flex justify-between items-center group"
                            >
                                <span className="text-sm font-bold text-gray-200 group-hover:text-white">{u.username}</span>
                                {u.eaId && <span className="text-[10px] text-gray-500">{u.eaId}</span>}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export const SeasonManager: React.FC = () => {
    const { refreshData, seasons } = useLeague();
    const [selectedSeasonId, setSelectedSeasonId] = useState<string>('');
    const [data, setData] = useState<SeasonData | null>(null);
    const [loading, setLoading] = useState(true);
    
    // State for Global Assign Tool
    const [manualUser, setManualUser] = useState<string>('');
    const [manualTeam, setManualTeam] = useState<string>('');
    const [manualReserve, setManualReserve] = useState(false);

    const loadData = async (sid?: string) => {
        setLoading(true);
        try {
            const url = sid ? `season_drivers.php?seasonId=${sid}` : 'season_drivers.php';
            const res = await apiRequest<SeasonData>(url);
            setData(res);
            if (!selectedSeasonId && res.seasonId) {
                setSelectedSeasonId(res.seasonId);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData(selectedSeasonId);
    }, [selectedSeasonId]);

    // Generalized Assignment Function
    const performAssign = async (userId: string, teamId: string | null, isReserve: boolean) => {
        if (!userId || !selectedSeasonId) return;
        try {
            await apiRequest('season_drivers.php', 'POST', {
                userId,
                teamId, 
                isReserve,
                seasonId: selectedSeasonId
            });
            await loadData(selectedSeasonId);
            await refreshData();
            // Reset manual form if used
            setManualUser('');
        } catch (e: any) {
            alert("Error: " + e.message);
        }
    };

    const handleRemove = async (userId: string) => {
        if (!confirm("Remove driver from this season?")) return;
        try {
            await apiRequest(`season_drivers.php?userId=${userId}&seasonId=${selectedSeasonId}`, 'DELETE');
            await loadData(selectedSeasonId);
            await refreshData();
        } catch (e: any) {
            alert("Error: " + e.message);
        }
    };

    if (loading) return <div className="p-10 text-center"><RefreshCw className="animate-spin mx-auto"/> Fahrerfeld wird geladen …</div>;
    if (!data) return <div className="p-10 text-center">Keine aktive Saison gefunden. Bitte zuerst eine Saison im Assistenten anlegen.</div>;

    // Filter users who are NOT yet assigned
    const assignedUserIds = data.assignments.map(a => a.user_id);
    const availableUsers = data.users.filter(u => !assignedUserIds.includes(u.id!));

    // Split Assignments
    const globalReserves = data.assignments.filter(a => !a.team_id);
    
    return (
        <div className="mx-auto max-w-7xl space-y-6 animate-fade-in pb-20">
            <PageHero
                eyebrow={data.seasonName}
                title="Stammfahrer & Teams"
                description="Fahrer den Saisonteams zuweisen, Ersatzfahrer verwalten und die Besetzung des Grids organisieren."
                icon={Users}
                accent="#22c55e"
                stats={[{ label: 'Teams', value: data.teams.length }, { label: 'Zugewiesen', value: data.assignments.length }, { label: 'Ersatzfahrer', value: globalReserves.length }]}
                actions={(
                    <div className="flex items-end gap-2">
                    <div className="flex flex-col">
                        <label className="text-[10px] uppercase font-bold text-gray-500 mb-1">Saison auswählen</label>
                        <select 
                            className="bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white outline-none focus:border-f1-red"
                            value={selectedSeasonId}
                            onChange={e => setSelectedSeasonId(e.target.value)}
                        >
                            {seasons.map(s => (
                                <option key={s.id} value={s.id}>{s.name} ({s.year})</option>
                            ))}
                        </select>
                    </div>
                    <Button variant="ghost" icon={RefreshCw} onClick={() => loadData(selectedSeasonId)}>Aktualisieren</Button>
                    </div>
                )}
            />

            {/* Global Assignment Tool (Advanced/Manual) */}
            <div className="rounded-[1.75rem] border border-white/[0.075] bg-gradient-to-br from-[#171a24] to-[#0e1017] p-6 shadow-[0_24px_70px_rgba(0,0,0,0.22)]">
                <h3 className="text-lg font-bold uppercase mb-4 flex items-center gap-2">
                    <UserPlus size={18} className="text-f1-red"/> Manuelle Zuweisung / Ersatzfahrer
                </h3>
                <div className="flex flex-col md:flex-row gap-4 items-end">
                    <div className="flex-1 w-full">
                        <label className="block text-xs uppercase font-bold text-gray-500 mb-1">Fahrer</label>
                        <DriverAutocomplete 
                            users={availableUsers} 
                            onSelect={setManualUser} 
                            placeholder="Verfügbaren Fahrer suchen …"
                        />
                    </div>
                    
                    <div className="flex-1 w-full">
                        <label className="block text-xs uppercase font-bold text-gray-500 mb-1">Team</label>
                        <select 
                            className="w-full bg-gray-900 border border-gray-600 rounded p-2 text-white h-[38px]"
                            value={manualTeam}
                            onChange={e => setManualTeam(e.target.value)}
                        >
                        <option value="">Globaler Ersatzfahrer (ohne Team)</option>
                            {data.teams.map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="mb-2">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                            <input 
                                type="checkbox" 
                                className="w-4 h-4 rounded bg-gray-700 border-gray-500 text-f1-red focus:ring-0"
                                checked={manualReserve || !manualTeam}
                                disabled={!manualTeam} 
                                onChange={e => setManualReserve(e.target.checked)}
                            />
                            <span className="font-bold text-sm text-gray-300">Reserve?</span>
                        </label>
                    </div>

                    <Button onClick={() => performAssign(manualUser, manualTeam || null, manualReserve || !manualTeam)} disabled={!manualUser}>
                        <Save className="w-4 h-4 mr-2"/> Zuweisen
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {data.teams.map(team => {
                    const teamDrivers = data.assignments.filter(a => a.team_id === team.id && a.is_reserve == 0);
                    const teamReserves = data.assignments.filter(a => a.team_id === team.id && a.is_reserve == 1);

                    return (
                        <div key={team.id} className="bg-f1-carbon border border-gray-800 rounded-lg flex flex-col shadow-lg">
                            <div className="h-1.5 w-full rounded-t-lg" style={{ backgroundColor: team.color }}></div>
                            <div className="p-4 bg-gray-900/50 flex justify-between items-center border-b border-gray-800">
                                <h3 className="font-bold uppercase">{team.name}</h3>
                                {team.logoUrl && <img src={team.logoUrl} className="h-6 object-contain" alt="logo"/>}
                            </div>
                            
                            <div className="p-4 space-y-3 flex-1">
                                {/* Main Drivers Slots */}
                                <div className="space-y-2">
                                    <div className="text-xs font-bold text-gray-500 uppercase">Main Seats</div>
                                    {[0, 1].map(seatIdx => {
                                        const driver = teamDrivers[seatIdx];
                                        return (
                                            <div key={seatIdx} className={`rounded ${driver ? 'p-2 bg-gray-800 border border-gray-700' : 'bg-gray-900/30 border border-dashed border-gray-700'}`}>
                                                {driver ? (
                                                    <div className="flex justify-between items-center">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-6 h-6 rounded bg-gray-700 flex items-center justify-center text-xs font-bold">
                                                                {driver.username.charAt(0)}
                                                            </div>
                                                            <div className="text-sm font-bold truncate max-w-[120px]">{driver.username}</div>
                                                        </div>
                                                        <button onClick={() => handleRemove(driver.user_id)} className="text-gray-500 hover:text-red-500"><Trash2 size={14}/></button>
                                                    </div>
                                                ) : (
                                                    <DriverAutocomplete 
                                                        users={availableUsers}
                                                        onSelect={(uid) => performAssign(uid, team.id, false)}
                                                        placeholder="+ Fahrer hinzufügen"
                                                        className="w-full"
                                                    />
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>

                                {/* Team Reserves (if any) */}
                                {teamReserves.length > 0 && (
                                    <div className="mt-2 pt-2 border-t border-gray-800">
                                        <div className="text-xs font-bold text-gray-500 uppercase mb-1">Team Reserves</div>
                                        {teamReserves.map(r => (
                                            <div key={r.id} className="flex justify-between items-center text-sm text-gray-400 py-1 px-2 hover:bg-gray-800 rounded">
                                                <span>{r.username}</span>
                                                <button onClick={() => handleRemove(r.user_id)} className="text-gray-600 hover:text-red-500"><Trash2 size={12}/></button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                
                                {/* Quick Add Team Reserve */}
                                <div className="pt-2">
                                     <div className="text-xs font-bold text-gray-600 uppercase mb-1">Add Reserve</div>
                                     <DriverAutocomplete 
                                        users={availableUsers}
                                        onSelect={(uid) => performAssign(uid, team.id, true)}
                                        placeholder="+ Team Reserve"
                                        className="opacity-50 hover:opacity-100 transition-opacity"
                                    />
                                </div>
                            </div>
                        </div>
                    )
                })}

                {/* Global Reserve Pool Card */}
                <div className="bg-gray-800/30 border border-dashed border-gray-600 rounded-lg flex flex-col">
                    <div className="p-4 bg-gray-900/30 flex justify-between items-center border-b border-gray-800">
                        <h3 className="font-bold uppercase text-gray-400">Globaler Ersatzfahrer-Pool</h3>
                        <ShieldAlert className="text-gray-500 w-5 h-5"/>
                    </div>
                    <div className="p-4 flex-1">
                        <div className="mb-4">
                            <DriverAutocomplete 
                                users={availableUsers}
                                onSelect={(uid) => performAssign(uid, null, true)}
                                placeholder="+ Ersatzfahrer hinzufügen"
                            />
                        </div>

                        {globalReserves.length > 0 ? (
                            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                                {globalReserves.map(r => (
                                    <div key={r.id} className="p-2 rounded border border-gray-700 bg-gray-900/50 flex justify-between items-center">
                                         <div className="flex items-center gap-2">
                                            <div className="w-6 h-6 rounded bg-gray-800 flex items-center justify-center text-xs font-bold text-gray-400">R</div>
                                            <div>
                                                <div className="text-sm font-bold text-white">{r.username}</div>
                                            </div>
                                        </div>
                                        <button onClick={() => handleRemove(r.user_id)} className="text-gray-500 hover:text-red-500"><Trash2 size={14}/></button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center text-sm text-gray-500 italic py-4">
                                No global reserves assigned.
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};
